import React, { createContext, useContext, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { findPage, PATHS, SITE_URL } from '../routes';

export const HeadContext = createContext(null);
const OG_IMAGE = `${SITE_URL}/og-image.png?v=2`;

const upsert = (selector, create, attrs) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement(create);
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
};

export const buildHead = ({ title, description, pathname, lang, jsonLd, noindex }) => {
  const page = findPage(pathname);
  const alternates = page && !noindex ? { en: PATHS[page.key].en, es: PATHS[page.key].es } : null;
  return {
    title,
    description,
    lang,
    canonical: `${SITE_URL}${page ? PATHS[page.key][lang] : pathname}`,
    alternates,
    jsonLd,
    noindex,
  };
};

export const headToHtml = (h) => {
  const esc = (s) => String(s).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
  const tags = [
    `<title>${esc(h.title)}</title>`,
    `<meta name="description" content="${esc(h.description)}" />`,
    `<link rel="canonical" href="${h.canonical}" />`,
    `<meta property="og:type" content="website" />`,
    `<meta property="og:site_name" content="Bullish Accounting" />`,
    `<meta property="og:title" content="${esc(h.title)}" />`,
    `<meta property="og:description" content="${esc(h.description)}" />`,
    `<meta property="og:url" content="${h.canonical}" />`,
    `<meta property="og:image" content="${OG_IMAGE}" />`,
    `<meta property="og:image:type" content="image/png" />`,
    `<meta property="og:image:width" content="1200" />`,
    `<meta property="og:image:height" content="630" />`,
    `<meta property="og:image:alt" content="BULLISH — Tax &amp; Advisory. Strategy Behind Every Decision." />`,
    `<meta property="og:locale" content="${h.lang === 'es' ? 'es_US' : 'en_US'}" />`,
    `<meta property="og:locale:alternate" content="${h.lang === 'es' ? 'en_US' : 'es_US'}" />`,
    `<meta name="twitter:card" content="summary_large_image" />`,
    `<meta name="twitter:title" content="${esc(h.title)}" />`,
    `<meta name="twitter:description" content="${esc(h.description)}" />`,
    `<meta name="twitter:image" content="${OG_IMAGE}" />`,
  ];
  if (h.noindex) tags.push('<meta name="robots" content="noindex, nofollow" />');
  if (h.alternates) {
    tags.push(`<link rel="alternate" hreflang="en" href="${SITE_URL}${h.alternates.en}" />`);
    tags.push(`<link rel="alternate" hreflang="es" href="${SITE_URL}${h.alternates.es}" />`);
    tags.push(`<link rel="alternate" hreflang="x-default" href="${SITE_URL}${h.alternates.en}" />`);
  }
  if (h.jsonLd) tags.push(`<script type="application/ld+json">${JSON.stringify(h.jsonLd).replace(/</g, '\\u003c')}</script>`);
  return tags.join('\n    ');
};

export const Seo = ({ title, description, jsonLd, noindex = false }) => {
  const { pathname } = useLocation();
  const ctx = useContext(HeadContext);
  const lang = pathname.startsWith('/es') ? 'es' : 'en';
  const head = buildHead({ title, description, pathname, lang, jsonLd, noindex });
  if (ctx) ctx.head = head;

  useEffect(() => {
    document.title = head.title;
    document.documentElement.lang = head.lang;
    upsert('meta[name="description"]', 'meta', { name: 'description', content: head.description });
    upsert('link[rel="canonical"]', 'link', { rel: 'canonical', href: head.canonical });
    upsert('meta[property="og:title"]', 'meta', { property: 'og:title', content: head.title });
    upsert('meta[property="og:description"]', 'meta', { property: 'og:description', content: head.description });
    upsert('meta[property="og:url"]', 'meta', { property: 'og:url', content: head.canonical });
    document.head.querySelectorAll('meta[name="robots"]').forEach((el) => el.remove());
    if (head.noindex) upsert('meta[name="robots"]', 'meta', { name: 'robots', content: 'noindex, nofollow' });
    document.head.querySelectorAll('link[rel="alternate"][hreflang]').forEach((el) => el.remove());
    if (head.alternates) {
      [['en', head.alternates.en], ['es', head.alternates.es], ['x-default', head.alternates.en]].forEach(([hl, href]) => {
        const el = document.createElement('link');
        el.rel = 'alternate';
        el.hreflang = hl;
        el.href = `${SITE_URL}${href}`;
        document.head.appendChild(el);
      });
    }
  }, [head.title, head.description, head.canonical, head.lang, head.alternates, head.noindex]);

  return null;
};
